/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { FormEvent } from "react";
import { MaskFunction } from '@/shared/utils/input-masks';
import { useFormStoreSetters, useFormStoreFieldData } from '../FormStore';
import type { FieldData, FieldValidationFn, FormSetters } from '@/shared/types/form-store';
import { cn } from "@/shared/ui/utils/tailwind";

export type OnFieldChange = (
  fieldData: FieldData,
  setters: FormSetters
) => void;

export type OnFieldFocus = (fieldData: FieldData, setters: FormSetters) => void;

export type MaskBehavior = "raw" | "masked";

export type InputFillProps = {
  name: string;
  forwardRef?: React.RefObject<HTMLInputElement>;
  placeholder?: string;
  mask?: MaskFunction;
  maskPersistBehavior?: MaskBehavior;
  validation?: FieldValidationFn;
  validationMoment?: "blur" | "change" | "both";
  onFieldChange?: OnFieldChange;
  onFieldBlur?: OnFieldFocus;
  onFieldFocus?: OnFieldFocus;
  asTextArea?: boolean;
};

const InputFill: Component<InputFillProps> = ({
  name,
  placeholder,
  mask,
  maskPersistBehavior = "raw",
  validation,
  validationMoment = "change",
  onFieldChange,
  onFieldBlur,
  onFieldFocus,
  asTextArea = false,
  forwardRef,
  sx,
  ...props
}) => {
  const fieldData = useFormStoreFieldData(name);
  const { value, error, state = {} } = fieldData;
  const { isDisabled = false } = state;
  const setters = useFormStoreSetters();
  const { setFieldValue, setFieldState, setFieldError } = setters;

  const renderValue = mask?.(value ?? "").masked ?? value ?? "";

  if (asTextArea) props.type = "text";

  // Helper function to apply mask to value
  function applyMask(inputValue: string): string {
    if (!mask) {
      return inputValue;
    }
    const { unmasked, masked } = mask(inputValue);
    return maskPersistBehavior == "masked" ? masked : unmasked;
  }

  // Helper function to validate value if needed
  function validateValueIfNeeded(inputValue: string): string | undefined {
    if (["change", "both"].includes(validationMoment)) {
      return validation?.({ ...fieldData, value: inputValue });
    }
    return error;
  }

  // Helper function to update field if value or error changed
  function updateFieldIfChanged(newValue: string, newError: string | undefined): void {
    if (value !== newValue || newError !== error) {
      setFieldValue(name, newValue);
      setFieldError(name, newError);
      onFieldChange?.(
        { ...fieldData, value: newValue, error: newError },
        setters
      );
    }
  }

  const handleInputChange = (event: FormEvent<HTMLInputElement>) => {
    let newValue = event.currentTarget.value;
    newValue = applyMask(newValue);
    const newError = validateValueIfNeeded(newValue);
    updateFieldIfChanged(newValue, newError);
  };

  const handleBlur = (event: FormEvent<HTMLInputElement>) => {
    setFieldState(name, { isFocused: false });

    let newValue = event.currentTarget.value;
    let newError = error;

    if (["blur", "both"].includes(validationMoment)) {
      newError = validation?.({ ...fieldData, value: newValue });
      if (newError !== error) setFieldError(name, newError);
    }

    onFieldBlur?.({ ...fieldData, value: newValue, error: newError }, setters);
  };

  const handleFocus = (event: FormEvent<HTMLInputElement>) => {
    setFieldState(name, { isFocused: true });
    onFieldFocus?.(fieldData, setters);
  };

  const Component = asTextArea ? 'textarea' : 'input';

  return (
    <Component
      ref={forwardRef as any}
      disabled={isDisabled}
      name={name}
      id={name}
      className={cn(
        "appearance-none px-1 border-none outline-none flex-grow flex-shrink basis-0",
        "h-full max-w-full bg-transparent resize-none",
        isDisabled && "bg-[var(--surface-tertiary)]"
      )}
      value={renderValue}
      onBlur={handleBlur as any}
      onFocus={handleFocus as any}
      onChange={handleInputChange as any}
      placeholder={placeholder}
      {...props}
    />
  );
};
export default InputFill;
